import { type FC, useState } from "react";

import { CourseCodeGroupingDropdown } from "./CourseCodeGroupingDropdown";

import { COURSE_CODE_ID_QUERY_PARAM } from "components/constants";
import { Trans, t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { TextField } from "mds/components/TextField";
import { storeApi } from "store/index";
import { toastSuccessMessage } from "utils/alerts";
import { useQueryParamsManager } from "utils/routing";

type AddCourseCodeModalProps = {
  eventName?: string;
  eventProperties?: Record<string, unknown>;
  localCourseCodes: Array<{ order?: number }>;
  onClose: () => void;
  defaultGroupingId?: string | null;
};

export const AddCourseCodeModal: FC<AddCourseCodeModalProps> = ({
  eventName,
  eventProperties,
  localCourseCodes,
  onClose,
  defaultGroupingId,
}) => {
  const [name, setName] = useState("");
  const [selectedGroupingId, setSelectedGroupingId] = useState<string | null>(
    defaultGroupingId || null,
  );
  const { setQueryParams } = useQueryParamsManager();

  const handleSubmit = () => {
    if (!name.trim()) {
      return;
    }

    const newOrder = (localCourseCodes[localCourseCodes.length - 1]?.order || 0) + 1;
    storeApi.course_codes
      .create({
        grouping_id: selectedGroupingId,
        order: newOrder,
        title: name.trim(),
      })
      .then((newItem) => {
        toastSuccessMessage(t("home.toasts.course_code_added"));
        setQueryParams({ [COURSE_CODE_ID_QUERY_PARAM]: newItem.id });
        onClose();
      });
  };

  return (
    <div className="mt-8 flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <label className="flex flex-col gap-1">
          <div>{t("nav_list.new_title_course_code")}</div>
          <TextField
            eventName={eventName}
            eventProperties={eventProperties}
            placeholder={t("home.placeholder.course_code_name")}
            value={name}
            isEditing
            onChange={(e) => setName(String(e.target.value))}
            onKeyDown={(e) => {
              if (e.key === "Enter" && name.trim()) {
                handleSubmit();
              }
            }}
          />
        </label>
      </div>

      <div className="flex flex-col gap-2">
        <div className="font-medium">
          <Trans i18nKey="home.course_codes.add_to_grouping_label">
            Add to Folder
            <span className="font-normal text-black-tint-55">(Optional)</span>
          </Trans>
        </div>

        <CourseCodeGroupingDropdown
          selectedGroupingId={selectedGroupingId}
          onSelectionChange={setSelectedGroupingId}
        />
      </div>

      <div className="flex w-full justify-end gap-2">
        <Button kind="white" size="s" title={t("common.cancel")} onClick={onClose}>
          {t("common.cancel")}
        </Button>

        <Button
          disabled={!name.trim()}
          kind="blue"
          size="s"
          title={t("common.add.course_code")}
          onClick={handleSubmit}
        >
          {t("common.add.course_code")}
        </Button>
      </div>
    </div>
  );
};
